import { useEffect, useState } from "react";
import { Loader2, Monitor, PhoneForwarded, Smartphone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { LiveCall } from "../../../lib/aiLive";
import { getMyMobile } from "../../../lib/calls";
import { startAiTakeover, takeoverError, type TakeoverError } from "../../../lib/aiTakeover";
import { formatPhone } from "../../../lib/phone";
import { Note } from "../../sections/settings/panel/fields";
import { Btn } from "../../ui";
import { useCallContext } from "../callContext";
import { canStartCall } from "../callState";

/**
 * "Take over": the operator asks to be put on the call the AI is having (R6).
 *
 * Two ways in, and the operator picks one every time (the owner's decision):
 *
 *  - IN THIS BROWSER (ring R6d). The CRM's own call window opens on the AI's
 *    call and puts this browser through to it; from there the window is the
 *    whole story, including why it did not work. Offered only while this
 *    browser has no call of its own up - the provider would refuse the press on
 *    the same rule (`canStartCall`).
 *  - MY MOBILE (ring R6b). The press rings the operator's own mobile, and only
 *    when they answer and press one does the AI say its line and step out. The
 *    card's own state line follows the attempt from there.
 *
 * Either way the button's job is to be pressed once and get out of the way.
 *
 * The mobile number is read once, when the menu opens, so an operator who has
 * never saved one is told here rather than by a refusal from the server.
 */
export function AiTakeoverButton({ call }: { call: LiveCall }) {
  const t = useT();
  const ctx = useCallContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TakeoverError | null>(null);
  const [mobile, setMobile] = useState<string | null>(null);

  useEffect(() => {
    if (!open || mobile !== null) return;
    let alive = true;
    getMyMobile()
      .then((answer) => {
        if (alive) setMobile(answer.mobileNumber ?? "");
      })
      .catch(() => {
        // The server decides anyway: an empty read only costs the hint.
        if (alive) setMobile("");
      });
    return () => {
      alive = false;
    };
  }, [open, mobile]);

  // A call already up in this window - the operator's own, or an ended one
  // still being written up does not count - is a window with no room for this.
  const browserFree = ctx !== null && ctx.enabled && canStartCall(ctx.state.phase);

  function pressBrowser() {
    if (!ctx || !browserFree) return;
    setError(null);
    setOpen(false);
    ctx.takeOverAiCall({
      callLogId: call.id,
      companyName: call.companyName,
      regCode: call.regCode,
      toNumber: call.toNumber,
    });
  }

  async function pressMobile() {
    setBusy(true);
    setError(null);
    try {
      await startAiTakeover(call.id, "mobile");
      setOpen(false);
    } catch (err) {
      setError(takeoverError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Btn small>
            <PhoneForwarded className="size-3.5" />
            {t("aiCalls.live.takeover.button")}
          </Btn>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <Btn small className="w-full justify-start" disabled={!browserFree || busy} onClick={pressBrowser}>
            <Monitor className="size-3.5" />
            {t("aiCalls.live.takeover.viaBrowser")}
          </Btn>
          <p className="crm-sub mb-2 mt-1 text-xs">
            {t(browserFree ? "aiCalls.live.takeover.browserHint" : "aiCalls.live.takeover.browserBusy")}
          </p>
          <Btn small className="w-full justify-start" disabled={busy || mobile === ""} onClick={pressMobile}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Smartphone className="size-3.5" />}
            {t("aiCalls.live.takeover.viaMobile")}
          </Btn>
          <p className="crm-sub mt-1 text-xs">
            {mobile ? formatPhone(mobile) : t("aiCalls.live.takeover.noMobile")}
          </p>
        </PopoverContent>
      </Popover>
      {error ? <Note tone="warn">{t(`aiCalls.live.takeover.error.${error}`)}</Note> : null}
    </>
  );
}
